import { useEffect, useState } from "react";
import { Plus, Share2, Home as HomeIcon, ExternalLink, Sparkles, Upload, Trash2, LayoutList, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import PropertyQuickAdd from "../components/PropertyQuickAdd";
import ShareModal from "../components/ShareModal";
import NewCollectionModal from "../components/NewCollectionModal";

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuick, setShowQuick] = useState(false);
  const [editing, setEditing] = useState(null); // property being edited
  const [shareFor, setShareFor] = useState(null); // property object
  const [showCollection, setShowCollection] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/properties");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

   
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this property? Anything shared with clients from it will still be viewable via existing links until you revoke each collection.")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">My Properties</h1>
          <p className="mt-1 text-sm text-slate-500">Your own inventory — the listings you own, co-own, or represent. Client recommendations live under <b>MLS Search</b>.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            onClick={() => setShowCollection(true)}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            data-testid="new-collection-btn"
          >
            <LayoutList className="h-4 w-4" /> {selected.size > 0 ? `Share ${selected.size} as collection` : "New collection"}
          </button>
          <button
            onClick={() => setShowQuick(true)}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white"
            style={{ background: "var(--primary-hex)" }}
            data-testid="add-property-btn"
          >
            <Plus className="h-4 w-4" /> Add property
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <HomeIcon className="mx-auto h-8 w-8 text-slate-400" />
          <h3 className="mt-3 font-heading text-lg font-semibold text-slate-900">No properties yet</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Add your first listing — it takes under 20 seconds. Then share it with any lead and watch the score climb the moment they open, save, or ask for a showing.</p>
          <button onClick={() => setShowQuick(true)} className="mt-6 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="add-property-empty-btn">
            <Plus className="h-4 w-4" /> Add your first property
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <PropertyCard
              key={p.id}
              p={p}
              picked={selected.has(p.id)}
              onTogglePick={() => setSelected(s => {
                const next = new Set(s);
                if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                return next;
              })}
              onEdit={() => setEditing(p)}
              onShare={() => setShareFor(p)}
              onDelete={() => del(p.id)}
            />
          ))}
        </div>
      )}

      {showQuick && <PropertyQuickAdd onClose={() => setShowQuick(false)} onSaved={() => { setShowQuick(false); load(); }} />}
      {editing && <PropertyQuickAdd existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {shareFor && <ShareModal property={shareFor} onClose={() => setShareFor(null)} />}
      {showCollection && (
        <NewCollectionModal
          properties={items}
          defaultSelected={Array.from(selected)}
          onClose={() => setShowCollection(false)}
          onCreated={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}

function PropertyCard({ p, picked, onTogglePick, onEdit, onShare, onDelete }) {
  const price = p.list_price ? `$${Number(p.list_price).toLocaleString()}` : "—";
  const bbs = [p.beds && `${p.beds} bd`, p.baths && `${p.baths} ba`, p.sqft && `${Number(p.sqft).toLocaleString()} sqft`].filter(Boolean).join(" · ") || "";
  // Status pill palette — chosen so the whole inventory reads at a glance:
  // active = live listing, under_contract = deal in flight, sold = closed,
  // expired = needs attention. Unknown values fall back to slate.
  const statusStyles = {
    active:         { label: "Active",         cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    under_contract: { label: "Under Contract", cls: "bg-amber-50 text-amber-900 border-amber-200" },
    sold:           { label: "Sold",           cls: "bg-slate-900 text-white border-slate-900" },
    expired:        { label: "Expired",        cls: "bg-rose-50 text-rose-800 border-rose-200" },
  };
  const s = statusStyles[p.status] || { label: p.status || "Active", cls: "bg-slate-100 text-slate-800 border-slate-200" };
  return (
    <div className={`rounded-lg border bg-white overflow-hidden hover:shadow-md transition-shadow relative ${picked ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"}`} data-testid={`property-card-${p.id}`}>
      <label className="absolute top-2 right-2 z-10 h-7 w-7 rounded-md bg-white/90 backdrop-blur border border-slate-200 shadow-sm flex items-center justify-center cursor-pointer">
        <input
          type="checkbox"
          checked={!!picked}
          onChange={onTogglePick}
          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
          aria-label="Pick for collection"
          data-testid={`property-pick-${p.id}`}
        />
      </label>
      <div className="aspect-[4/3] bg-slate-100 relative">
        {p.hero_image_url ? (
          <img src={p.hero_image_url} alt={p.address_line1} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <HomeIcon className="h-12 w-12" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span
            className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border shadow-sm ${s.cls}`}
            data-testid={`property-status-${p.id}`}
          >
            {s.label}
          </span>
          {p.entry_method === "quick" && (
            <span className="text-[10px] font-semibold uppercase tracking-widest bg-white/90 backdrop-blur px-2 py-0.5 rounded">Quick add</span>
          )}
        </div>
      </div>
      <div className="p-4">
        <div className="font-heading font-semibold text-slate-900 truncate">{p.address_line1}</div>
        <div className="mt-0.5 text-xs text-slate-500 truncate">{[p.city, p.state].filter(Boolean).join(", ")}</div>
        <div className="mt-3 flex items-baseline justify-between gap-2">
          <div className="font-heading text-lg font-bold text-slate-900 tabular-nums">{price}</div>
          {bbs && <div className="text-xs text-slate-500">{bbs}</div>}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={onShare}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-2"
            data-testid={`share-btn-${p.id}`}
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
          {p.listing_url && (
            <a href={p.listing_url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-slate-700" title="Open listing">
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <button onClick={onEdit} className="p-2 text-slate-400 hover:text-slate-900" title="Edit" data-testid={`edit-btn-${p.id}`}>
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="p-2 text-slate-400 hover:text-red-700" title="Delete" data-testid={`delete-btn-${p.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
